import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeftIcon, ChevronRightIcon, MegaphoneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "../../api/axios";
import { usePolling } from "../../hooks/usePolling";

const FEED_POLL_MS = 60000;

export function CompanyAnnouncements() {
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const userId = useSelector((state) => state.session.currentUser?.id);
  if (!companyId || !userId) return null;
  return <AnnouncementFeed key={`${companyId}:${userId}`} />;
}

function AnnouncementFeed() {
  const [messages, setMessages] = useState([]);
  const loadRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    let pending = false;
    const load = async () => {
      if (pending) return;
      pending = true;
      try {
        const res = await axios.get("/company_announcements/feed");
        if (!cancelled) setMessages(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch {
        // Do not keep stale audience/publication data after an unsuccessful refresh.
        if (!cancelled) setMessages([]);
      } finally { pending = false; }
    };
    loadRef.current = load;
    load();
    return () => { cancelled = true; loadRef.current = null; };
  }, []);
  usePolling(() => loadRef.current?.(), FEED_POLL_MS);
  return <FloatingAnnouncements messages={messages} />;
}

const messageVersion = (message) => JSON.stringify([message.id, message.title, message.body, message.starts_at, message.ends_at]);
const control = "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-amber-800 transition-colors hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700 disabled:opacity-30";

export function FloatingAnnouncements({ messages = [] }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState([]);
  const [position, setPosition] = useState(0);
  const visible = messages.filter((message) => !dismissed.includes(messageVersion(message)));
  const index = Math.min(position, Math.max(0, visible.length - 1));
  const message = visible[index];
  if (!message) return null;
  return createPortal(
    <aside aria-label={t("announcements.title")} className="pointer-events-none fixed inset-x-0 z-[90] flex justify-center px-4" style={{ top: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}>
      <div className="pointer-events-auto flex w-full max-w-xl items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-lg">
        <MegaphoneIcon className="mt-1 h-5 w-5 shrink-0 text-amber-800" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm">
          <div aria-live="polite" aria-atomic="true">
            <h2 className="break-words font-semibold text-amber-950">{message.title}</h2>
            <p className="mt-1 max-h-[min(40dvh,18rem)] overflow-y-auto whitespace-pre-wrap break-words pr-1 leading-6 text-amber-900">{message.body}</p>
          </div>
          {visible.length > 1 && <nav aria-label={t("announcements.title")} className="mt-2 flex items-center justify-between border-t border-amber-200/70 pt-1">
            <span className="text-xs font-medium tabular-nums text-amber-800">{index + 1} / {visible.length}</span>
            <div className="flex gap-1">
              <button type="button" className={control} aria-label={t("announcements.previousMessage")} disabled={index === 0} onClick={() => setPosition(index - 1)}><ChevronLeftIcon className="h-4 w-4" aria-hidden="true" /></button>
              <button type="button" className={control} aria-label={t("announcements.nextMessage")} disabled={index === visible.length - 1} onClick={() => setPosition(index + 1)}><ChevronRightIcon className="h-4 w-4" aria-hidden="true" /></button>
            </div>
          </nav>}
        </div>
        <button type="button" className={`${control} -mr-2 -mt-2`} aria-label={t("Close")} onClick={() => { setDismissed((old) => [...old, messageVersion(message)]); setPosition(index); }}><XMarkIcon className="h-4 w-4" aria-hidden="true" /></button>
      </div>
    </aside>, document.body
  );
}
